import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronDown, ChevronRight, Users, Bookmark, BookmarkCheck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Breadcrumb from '../components/Breadcrumb';
import { useWatchlist } from '../context/WatchlistContext';
import { api } from '../api';

const SIZE_OPTIONS = [
  { value: '1', label: '1 facility' },
  { value: '2-9', label: '2–9 facilities' },
  { value: '10-49', label: '10–49 facilities' },
  { value: '50+', label: '50+ facilities' },
];

function Select({ value, onChange, options, placeholder }) {
  return (
    <div className="relative">
      <select value={value} onChange={(e) => onChange(e.target.value)} className="appearance-none w-full bg-white border border-[#E5DCC4] rounded-lg h-11 px-3.5 pr-9 text-[13px] text-[#1A2622] outline-none cursor-pointer focus:border-[#4A7C5E]">
        <option value="">{placeholder}</option>
        {options.map((o, i) => <option key={i} value={typeof o === 'string' ? o : o.value}>{typeof o === 'string' ? o : o.label}</option>)}
      </select>
      <ChevronDown size={14} className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[#7A857F]"/>
    </div>
  );
}

export default function OwnersPage() {
  const [q, setQ] = useState('');
  const [type, setType] = useState('');
  const [size, setSize] = useState('');
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const { isWatched, toggle } = useWatchlist();
  const LIMIT = 60;

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.owners({ q: q || undefined, type: type || undefined, size: size || undefined, limit: LIMIT, offset: page * LIMIT })
        .then((d) => { setData(d); setLoading(false); })
        .catch(() => setLoading(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, type, size, page]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / LIMIT));

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="max-w-[1280px] mx-auto px-6 lg:px-8 pt-6 pb-10">
        <Breadcrumb items={[{ label: 'Home', to: '/' }, { label: 'Owners' }]} />

        <div className="mt-6">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md border border-[#4A7C5E]/30 bg-white">
            <Users size={12} className="text-[#4A7C5E]"/>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">CMS Owners</span>
          </div>
          <h1 className="mt-4 font-display text-4xl font-semibold text-[#1A2622] tracking-tight">Ownership records</h1>
          <p className="mt-3 text-[15px] text-[#3A4A44] max-w-3xl leading-relaxed">Distinct owner records from the CMS Ownership dataset, ranked by facility count and searchable by name, type, and portfolio size.</p>
        </div>

        <div className="mt-7 h-px bg-[#E5DCC4]"/>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-12 gap-3">
          <div className="md:col-span-6 relative">
            <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#7A857F]"/>
            <input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} placeholder="Search owners by name" className="w-full bg-white border border-[#E5DCC4] rounded-lg h-11 pl-10 pr-3 text-[13px] text-[#1A2622] placeholder:text-[#A8B0AA] outline-none focus:border-[#4A7C5E]"/>
          </div>
          <div className="md:col-span-3"><Select value={type} onChange={(v) => { setType(v); setPage(0); }} options={['Individual', 'Organization']} placeholder="All types"/></div>
          <div className="md:col-span-3"><Select value={size} onChange={(v) => { setSize(v); setPage(0); }} options={SIZE_OPTIONS} placeholder="Any portfolio size"/></div>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users size={13} className="text-[#4A7C5E]"/>
            <span className="text-[11px] font-semibold uppercase tracking-wider text-[#4A7C5E]">All owners</span>
          </div>
          <span className="text-xs text-[#7A857F]">{loading ? 'loading…' : `Showing ${page*LIMIT + 1}-${Math.min((page+1)*LIMIT, data.total)} of ${(data.total || 0).toLocaleString()}`}</span>
        </div>

        <ul className="mt-4 space-y-2">
          {data.items.map((o) => {
            const watched = isWatched('owner', o.slug);
            return (
              <li key={o.slug}>
                <div className="group flex items-center justify-between px-4 py-3 rounded-lg bg-white border border-[#E5DCC4] hover:border-[#4A7C5E] transition-colors">
                  <Link to={`/owners/${o.slug}`} className="flex items-center gap-3.5 min-w-0 flex-1">
                    <div className="w-10 h-10 rounded-full bg-[#F0E7D2] flex items-center justify-center text-[11px] font-semibold text-[#2D5F4F] shrink-0">{o.initials || '?'}</div>
                    <div className="min-w-0">
                      <div className="text-[14px] font-medium text-[#1A2622] group-hover:text-[#2D5F4F] truncate">{o.name}</div>
                      <div className="text-[11px] text-[#7A857F] mt-0.5">{o.facilities} {o.facilities === 1 ? 'facility' : 'facilities'} · {o.states} {o.states === 1 ? 'state' : 'states'}</div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium border ${(o.type || '').toLowerCase().includes('organization') ? 'border-[#C9D4CC] bg-[#EAF1E5] text-[#2D5F4F]' : 'border-[#E5DCC4] bg-[#FBF6EC] text-[#3A4A44]'}`}>{o.type || 'Individual'}</span>
                    <button onClick={() => toggle({ kind: 'owner', slug: o.slug, name: o.name })} className={`p-1.5 rounded-md ${watched ? 'bg-[#4A7C5E] text-white' : 'text-[#7A857F] hover:bg-[#F5EFDD] hover:text-[#1A2622]'}`}>
                      {watched ? <BookmarkCheck size={13}/> : <Bookmark size={13}/>}
                    </button>
                    <ChevronRight size={14} className="text-[#A8B0AA] group-hover:text-[#4A7C5E] group-hover:translate-x-0.5 transition-all"/>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>

        <div className="mt-8 flex items-center justify-between text-[13px] text-[#5A6660]">
          <button onClick={() => setPage(Math.max(0, page-1))} disabled={page === 0} className="px-3.5 py-1.5 rounded-md border border-[#E5DCC4] bg-white hover:border-[#4A7C5E] disabled:opacity-40">Previous</button>
          <span>Page {page+1} of {totalPages.toLocaleString()}</span>
          <button onClick={() => setPage(page+1)} disabled={page+1 >= totalPages} className="px-3.5 py-1.5 rounded-md border border-[#E5DCC4] bg-white hover:border-[#4A7C5E] disabled:opacity-40">Next</button>
        </div>
      </main>
      <Footer />
    </div>
  );
}
